/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {forwardRef, useState, useRef, cloneElement} from "react";
import type {ComponentProps, ReactElement, ReactNode} from "react";
import {Link} from "react-router-dom";
import {addMessage} from "../features/messenger/messenger";
import {RawSpinner} from "../shared/ui/Spinner";
import {transitions} from "../styles/decoration.css";
import dsStyles from "../styles/index.css";
import {
  activeInteractionTheme,
  errorInteractionTheme,
  successInteractionTheme,
} from "../styles/themes/color-themes.css";
import {errorToString} from "../utils";
import cx from "../utils/cx";
import {useMyTransition} from "../utils/use-my-transition";
import {Col, css, Row} from "./Box";
import {buttonStyles as styles} from "./Button.css";
import Icon from "./Icon";

type ButtonStyles = typeof styles;
type ButtonState = "initial" | "loading" | "success" | "error";

const FadeIcon = ({
  icon,
  shown,
  withDelay,
}: {
  shown: boolean;
  icon: ReactElement<{className?: string}>;
  withDelay?: boolean;
}) => {
  const renderFn = useMyTransition(shown, shown, {timeoutMs: 500});
  return renderFn((present, leaving) =>
    present ? (
      <Col align="center" justify="center" absolute inset="0">
        {cloneElement<{className?: string}>(icon, {
          className: css(
            {applyTransforms: "true", setAnimDelay: withDelay ? "0.25s" : undefined},
            transitions.transformAndColors,
            leaving ? styles.movedUp : styles.animations.fadeIn
          ),
        })}
      </Col>
    ) : null
  );
};

const usePending = (opts: Pick<FullProps<unknown>, "onClick">) => {
  const [state, setIsPending] = useState<ButtonState>("initial");
  const currClickRef = useRef({});
  const handleClick: React.MouseEventHandler<HTMLElement> = (e) => {
    const clickFn = opts.onClick;
    if (!clickFn) return;
    const res = clickFn(e);
    if (typeof res?.then === "function") {
      const currClickObj = (currClickRef.current = {});
      setIsPending("loading");
      const setter = (val: ButtonState) => {
        if (currClickRef.current === currClickObj) setIsPending(val);
      };
      res.then(
        () => {
          setter("success");
          setTimeout(() => {
            setter("initial");
          }, 500);
        },
        (error: unknown) => {
          setter("error");
          setTimeout(() => {
            setter("initial");
          }, 2000);
          addMessage(errorToString(error), {type: "error"});
        }
      );
    }
  };
  return {
    handleClick,
    state,
    icons: (
      <>
        <FadeIcon shown={state === "loading"} icon={<RawSpinner size="20" />} withDelay />
        <FadeIcon shown={state === "success"} icon={<Icon name="check" size="20" />} />
        <FadeIcon shown={state === "error"} icon={<Icon name="close" size="20" />} />
      </>
    ),
  };
};

type MyOnClick = (e: React.MouseEvent<HTMLElement>) => void | Promise<void>;

type FullProps<T> =
  | (Omit<React.JSX.IntrinsicElements["button"], keyof T | "onClick"> &
      T & {href?: undefined; to?: undefined; onClick?: MyOnClick})
  | (Omit<React.JSX.IntrinsicElements["a"], keyof T | "onClick"> &
      T & {href: string; to?: undefined; disabled?: boolean; onClick?: MyOnClick})
  | (Omit<ComponentProps<typeof Link>, keyof T | "onClick"> &
      T & {
        href?: undefined;
        to: ComponentProps<typeof Link>["to"];
        disabled?: boolean;
        onClick?: MyOnClick;
      });

type SelfRawButtonProps = {
  variant?: keyof ButtonStyles["variant"];
  size?: keyof ButtonStyles["size"];
  // contentPadding?: null | "both" | "left" | "right";
  // iconColor?: null | "primary" | "secondary";
  active?: boolean;
  state?: ButtonState;
  onClick?: MyOnClick;
  // negatePadding?: boolean;
  // onMetaClick?: React.MouseEventHandler<HTMLButtonElement>;
};

const RawButton = forwardRef<HTMLElement, FullProps<SelfRawButtonProps>>((props, ref) => {
  const {className, disabled, size = "md", variant = "secondary", active, state, ...rest} = props;
  const getVariant = (): keyof ButtonStyles["variant"] => {
    if (active || state === "error" || state === "success") return "primary";
    return variant;
  };
  const getTheme = () => {
    if (state === "error") return errorInteractionTheme;
    if (state === "success") return successInteractionTheme;
    if (active) return activeInteractionTheme;
  };
  const classes = cx(
    styles.base,
    styles.size[size],
    styles.variant[getVariant()],
    (rest.to || rest.href) && styles.isLink,
    getTheme(),
    disabled && dsStyles.opacity["50%"],
    className
  );
  if (rest.to) {
    return (
      <Link
        className={classes}
        {...rest}
        {...(disabled ? {onClick: (e) => e.preventDefault()} : {})}
        ref={ref as any}
      />
    );
  } else if (rest.href) {
    return (
      <a className={classes} target="_blank" rel="noopener noreferrer" {...rest} ref={ref as any} />
    );
  } else {
    return (
      <button
        className={classes}
        type="button"
        disabled={!!disabled || state === "loading"}
        {...(rest as any)}
        ref={ref}
      />
    );
  }
});

type SelfButtonProps = Omit<SelfRawButtonProps, never>;

export const Button = forwardRef<HTMLElement, FullProps<SelfButtonProps>>((props, ref) => {
  const {onClick, children, ...rest} = props;
  const {handleClick, icons, state} = usePending({onClick});
  return (
    <RawButton state={state} {...rest} onClick={handleClick} ref={ref}>
      {icons}
      <div
        className={cx(
          dsStyles.applyTransforms.true,
          transitions.transformAndColors,
          state !== "initial" && styles.movedDown
        )}
      >
        {children}
      </div>
    </RawButton>
  );
});

export default Button;

// Pins its children to the bottom of the scroll area. The row itself lets taps through, so
// children that should be clickable need `pointerEvents: "auto"`.
export const FloatingArea = ({children}: {children: ReactNode}) => (
  <Col absolute inset="0" pointerEvents="none" justify="end">
    <Row
      sp="8"
      justify="end"
      align="center"
      px="16"
      className={css({position: "sticky", bottom: "16px"})}
    >
      {children}
    </Row>
  </Col>
);

export const FloatingAreaButton = forwardRef<HTMLElement, FullProps<SelfButtonProps>>(
  (props, ref) => (
    <Button
      variant="primary"
      size="floating"
      {...props}
      className={css({pointerEvents: "auto", flex: "none"}, props.className)}
      ref={ref}
    />
  )
);

export const FloatingButton = forwardRef<HTMLElement, FullProps<SelfButtonProps>>((props, ref) => (
  <FloatingArea>
    <FloatingAreaButton {...props} ref={ref} />
  </FloatingArea>
));
